// The two interactive dashboards, and the cross-filtering that makes them one
// screen rather than three grids on a page.
//
// SELECTING A ROW IN ONE PANE FILTERS THE SIBLINGS. `#59` names this as the
// least standard behaviour in the application and the most underestimated item
// in the phase, so it is worth being explicit about what it actually is here:
// the three panes share one dimension, the production order -- a barrier
// carries `order_id`, a two-week row carries `order_id`, a daily-production row
// carries `order_id`, and an order is one. So a selection is an order id, and
// whichever pane the person clicked, the other two narrow to that order and the
// selected row stays highlighted. Clicking the same row again clears it.
//
// That it is one shared key rather than a general filter graph is the reason it
// is a dozen lines instead of a framework. Nothing else in the application is
// shared between all three panes: `Plant` is on a barrier and not on a
// two-week row, `Week` is on a two-week row and not on a barrier.
//
// TABBED ON MOBILE. Three grids side by side need width that a phone does not
// have, so below 900px the panes become tabs and one is shown at a time. The
// selection survives the switch, which is what makes cross-filtering useful on
// a phone at all: select the order on one tab, read its barriers on the next.

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, type GridColumn } from '../grid/DataGrid';
import { ErrorNote } from '../shell/Screen';
import { useBarrierView, useCollection } from '../data';
import { barrierColumns, barrierRowClass } from '../views/barrierColumns';
import { dailyProductionColumns, ordersColumns } from '../views/productionScreens';
import { useOrderIndex, withOrderFacts } from '../views/orderIndex';
import { useIsNarrow } from '../shell/useIsNarrow';
import { applySelection, selectedIdIn, toggle, type PaneId, type Selection } from './selection';
import type { BarrierViewRow, Row } from '../types';

export function DashboardScreen({ variant }: { variant: 'dashboard' | 'daily' }) {
  const [selection, setSelection] = useState<Selection | null>(null);
  const narrow = useIsNarrow(900);
  const thirdPane: PaneId = variant === 'dashboard' ? 'barriers' : 'daily';
  const [tab, setTab] = useState<PaneId>('orders');

  const title = variant === 'dashboard' ? 'Dashboard' : 'Daily Production Tracker';
  const panes: PaneId[] = ['orders', 'two-week', thirdPane];
  const visible = narrow ? [tab] : panes;

  return (
    <section className="screen">
      <header className="screen-head">
        <div>
          <h1>{title}</h1>
          <p className="screen-description">
            Selecting a row narrows the other panes to its job number.
          </p>
        </div>
        <div className="screen-tools">
          {selection ? (
            <button className="link" onClick={() => setSelection(null)}>
              Clear filter: {selection.label}
            </button>
          ) : (
            <span className="muted">No filter</span>
          )}
        </div>
      </header>

      {narrow && (
        <nav className="tabs" role="tablist">
          {panes.map((pane) => (
            <button
              key={pane}
              role="tab"
              aria-selected={tab === pane}
              className={tab === pane ? 'tab active' : 'tab'}
              onClick={() => setTab(pane)}
            >
              {PANE_TITLES[pane]}
            </button>
          ))}
        </nav>
      )}

      <div className={narrow ? 'panes single' : `panes ${variant}`}>
        {visible.includes('orders') && (
          <OrdersPane selection={selection} onSelect={setSelection} />
        )}
        {visible.includes('two-week') && (
          <TwoWeekPane selection={selection} onSelect={setSelection} />
        )}
        {visible.includes('barriers') && (
          <BarriersPane selection={selection} onSelect={setSelection} />
        )}
        {visible.includes('daily') && <DailyPane selection={selection} onSelect={setSelection} />}
      </div>
    </section>
  );
}

const PANE_TITLES: Record<PaneId, string> = {
  orders: 'Orders View',
  'two-week': 'Two Week Production View',
  barriers: 'Barriers Dashboard',
  daily: 'Daily Production Tracker Table',
};

interface PaneProps {
  selection: Selection | null;
  onSelect: (selection: Selection | null) => void;
}

function Pane({ id, children }: { id: PaneId; children: ReactNode }) {
  return (
    <section className={`pane pane-${id}`}>
      <h2>{PANE_TITLES[id]}</h2>
      {children}
    </section>
  );
}

function OrdersPane({ selection, onSelect }: PaneProps) {
  const rows = useCollection('orders', { status: 'Open' });
  const index = useOrderIndex();
  const columns = useMemo(() => ordersColumns(), []);
  const resolved = useMemo(() => withOrderFacts(rows.data ?? [], index.data), [rows.data, index.data]);
  const shown = applySelection(resolved, selection, 'orders');

  return (
    <Pane id="orders">
      {rows.error && <ErrorNote error={rows.error} />}
      <DataGrid
        rows={shown}
        columns={columns}
        getRowId={(row) => String(row.order_id)}
        groupBy={['order_product_type']}
        sortBy={[{ key: 'order_number' }]}
        selectedId={selectedIdIn(selection, 'orders')}
        onSelect={(row) =>
          onSelect(
            toggle(selection, 'orders', row && {
              orderId: row.order_id,
              rowId: String(row.order_id),
              label: String(row.order_number ?? ''),
            })
          )
        }
        emptyMessage={rows.loading ? 'Loading…' : 'No open orders.'}
      />
    </Pane>
  );
}

function TwoWeekPane({ selection, onSelect }: PaneProps) {
  const rows = useCollection('two-week-production');
  const index = useOrderIndex();
  const resolved = useMemo(() => withOrderFacts(rows.data ?? [], index.data), [rows.data, index.data]);
  const shown = applySelection(resolved, selection, 'two-week');

  const columns = useMemo<GridColumn<Row>[]>(
    () => [
      { key: 'week_commencing', label: 'Week', type: 'date', width: 120 },
      { key: 'order_number', label: 'Job Number', type: 'str', width: 120 },
      { key: 'plan_build', label: 'Plan Build', type: 'int', width: 100, align: 'right' },
      { key: 'outlook_build', label: 'Outlook Build', type: 'int', width: 120, align: 'right', aggregate: 'sum' },
      { key: 'outlook_ship', label: 'Outlook Ship', type: 'int', width: 110, align: 'right' },
    ],
    []
  );

  return (
    <Pane id="two-week">
      {rows.error && <ErrorNote error={rows.error} />}
      <DataGrid
        rows={shown}
        columns={columns}
        getRowId={(row) => String(row.two_week_production_id)}
        groupBy={['week_commencing']}
        selectedId={selectedIdIn(selection, 'two-week')}
        onSelect={(row) =>
          onSelect(
            toggle(selection, 'two-week', row && {
              orderId: row.order_id,
              rowId: String(row.two_week_production_id),
              label: row.order_number === null || row.order_number === undefined ? null : String(row.order_number),
            })
          )
        }
        emptyMessage={rows.loading ? 'Loading…' : 'No two-week production rows.'}
      />
    </Pane>
  );
}

function BarriersPane({ selection, onSelect }: PaneProps) {
  const rows = useBarrierView('open', '', null);
  const navigate = useNavigate();
  const available = useMemo(() => barrierColumns({ editable: false, canSetHot: false, lookups: {} }), []);
  const columns = useMemo<GridColumn<BarrierViewRow>[]>(
    () =>
      ['jobNumber', 'dateOfEscalation', 'problem', 'owners', 'commodity', 'plant', 'currentCommitment'].map(
        (name) => available[name]
      ),
    [available]
  );
  const shown = applySelection(rows.data ?? [], selection, 'barriers');

  return (
    <Pane id="barriers">
      {rows.error && <ErrorNote error={rows.error} />}
      <DataGrid
        rows={shown}
        columns={columns}
        getRowId={(row) => String(row.barrier_number)}
        groupBy={[available.actionCategory.key]}
        groupDesc={[true]}
        sortBy={[{ key: available.commodity.key }]}
        rowClass={barrierRowClass}
        // Not `null`, which is what it was: hardcoded since the pane was
        // written, so the barriers pane could never show a selection however
        // right the selection was. The first sign-off reported it as part of
        // the wrong-key defect and it survived that fix, because it was a
        // second, unrelated bug with the same symptom.
        selectedId={selectedIdIn(selection, 'barriers')}
        onSelect={(row) => {
          // A barrier without an order cannot cross-filter -- `order_id` is
          // nullable and blank on part of the table -- so it opens instead.
          if (row && typeof row.order_id !== 'number') {
            navigate(`/data/barriers/${row.barrier_number}/edit`);
            return;
          }
          onSelect(
            toggle(selection, 'barriers', row && {
              orderId: row.order_id,
              rowId: String(row.barrier_number),
              label: row.order_number,
            })
          );
        }}
        emptyMessage={rows.loading ? 'Loading…' : 'No open barriers.'}
      />
    </Pane>
  );
}

function DailyPane({ selection, onSelect }: PaneProps) {
  const rows = useCollection('daily-production');
  const index = useOrderIndex();
  const columns = useMemo(
    () => dailyProductionColumns(index.data).map((column) => ({ ...column, editable: false })),
    [index.data]
  );
  const resolved = useMemo(() => withOrderFacts(rows.data ?? [], index.data), [rows.data, index.data]);
  const shown = applySelection(resolved, selection, 'daily');

  return (
    <Pane id="daily">
      {rows.error && <ErrorNote error={rows.error} />}
      <DataGrid
        rows={shown}
        columns={columns}
        getRowId={(row) => String(row.daily_production_id)}
        groupBy={['order_number']}
        sortBy={[{ key: 'production_date', desc: true }]}
        selectedId={selectedIdIn(selection, 'daily')}
        onSelect={(row) =>
          onSelect(
            toggle(selection, 'daily', row && {
              orderId: row.order_id,
              rowId: String(row.daily_production_id),
              label: row.order_number === null || row.order_number === undefined ? null : String(row.order_number),
            })
          )
        }
        emptyMessage={rows.loading ? 'Loading…' : 'No daily production rows.'}
      />
    </Pane>
  );
}
